/* Phase 10 UAT — inline flow that captures the mandatory Interview Confirmation Proof
   before an interview transitions from Scheduled → Confirmed.

   Flow:
     1. Ask the user to upload a file (any type — usually a screenshot / PDF of the recruiter's confirmation email).
     2. POST /documents/upload  → returns { id }
     3. POST /interviews/{iid}/confirmation-proof with { document_id }
     4. PUT /interviews/{iid} with { status: "Confirmed" }
     5. Emit onConfirmed() so the parent list refreshes.

   All state transitions are user-visible (Uploading… → Attaching proof… → Confirming…). */
import { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X, UploadSimple, CheckCircle, Paperclip } from "@phosphor-icons/react";

const STEP_LABEL = {
  idle: "Select the Interview Confirmation Proof to continue.",
  uploading: "Uploading proof…",
  attaching: "Linking proof to the interview…",
  flipping: "Flipping status to Confirmed…",
  done: "Interview confirmed.",
};

export default function ConfirmInterviewModal({ interview, onClose, onConfirmed }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("idle");
  const inputRef = useRef(null);

  const busy = ["uploading", "attaching", "flipping"].includes(step);

  const run = async () => {
    if (!file) { toast.error("Pick a proof file first"); return; }
    try {
      setStep("uploading");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "confirmation_email");
      fd.append("candidate_id", interview.candidate_id || "");
      const up = await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const docId = up.data?.id;
      if (!docId) throw new Error("Upload response did not contain a document id");

      setStep("attaching");
      await api.post(`/interviews/${interview.id}/confirmation-proof`, { document_id: docId });

      setStep("flipping");
      await api.put(`/interviews/${interview.id}`, { status: "Confirmed" });

      setStep("done");
      toast.success("Interview confirmed with proof");
      onConfirmed?.();
      onClose?.();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to confirm interview");
      setStep("idle");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center p-4 z-50"
         data-testid="confirm-interview-modal">
      <div className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-md relative">
        <button type="button" onClick={() => !busy && onClose?.()}
                data-testid="confirm-interview-close"
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 disabled:opacity-40"
                disabled={busy}><X size={18} /></button>

        <h3 className="font-semibold text-lg">Confirm Interview</h3>
        <p className="text-xs text-slate-500 mt-1">
          <b>Interview Confirmation Proof</b> is mandatory to flip status to Confirmed.
          The proof feeds the approval workflow: Executive → Group Lead Review → HR → Payout.
        </p>

        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
          <div className="text-slate-500">Interview</div>
          <div className="font-medium text-slate-800 mt-0.5">
            {interview.company} · {interview.job_title}
          </div>
          <div className="text-slate-500 mt-1">{interview.interview_date} · Round {interview.round}</div>
        </div>

        <div className="mt-4">
          <label className="text-xs text-slate-500">Proof file (screenshot, PDF, .eml, etc.)</label>
          <div className="mt-1 flex items-center gap-2">
            <input ref={inputRef} type="file" className="hidden"
                   data-testid="confirm-proof-file"
                   onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid="confirm-proof-pick"
                    className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              <UploadSimple size={14} /> {file ? "Change file" : "Choose file"}
            </button>
            {file && (
              <span className="text-xs text-slate-600 truncate inline-flex items-center gap-1"
                    data-testid="confirm-proof-filename">
                <Paperclip size={12} /> {file.name}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500 min-h-[1.25rem]" data-testid="confirm-step-status">
          {step === "done" ? (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircle size={12} /> {STEP_LABEL[step]}
            </span>
          ) : STEP_LABEL[step]}
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  data-testid="confirm-proof-cancel"
                  className="text-sm px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40">
            Cancel
          </button>
          <button type="button" onClick={run} disabled={busy || !file}
                  data-testid="confirm-proof-submit"
                  className="text-sm px-4 py-2 rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white inline-flex items-center gap-1">
            {busy ? STEP_LABEL[step] : "Upload & Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
